import { useState } from "react";
import { ArrowLeft, ArrowRight, CalendarDays, CreditCard, FileText, MessageCircle, Sparkles, User, X } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
  {
    icon: Sparkles,
    img: "/welcome/home.png",
    tag: "Welcome aboard",
    title: (first) => `Welcome to MyTrip${first ? `, ${first}` : ""}!`,
    body: (advisor) => `This is your private home for everything travel — one calm place to follow your trips, handle payments, and stay in touch with ${advisor}. Here's a quick tour of what you can do.`,
  },
  {
    icon: CalendarDays,
    img: "/welcome/trips.png",
    tag: "Your trips & countdown",
    title: () => "See every trip at a glance",
    body: () => "Your upcoming journey sits front and center with a live countdown, dates, and a day-by-day itinerary. Past and quoted trips stay neatly organized under My Trips.",
  },
  {
    icon: CreditCard,
    img: "/welcome/payments.png",
    tag: "Payments & invoices",
    title: () => "Payments made effortless",
    body: () => "Review your invoice and follow your payment plan — always knowing what's due and when. Tip: add a card on file in your Profile so settling deposits and final payments takes just a tap, with no scramble at the deadline.",
  },
  {
    icon: MessageCircle,
    img: "/welcome/messages.png",
    tag: "Message your advisor",
    title: (first, advisor) => `Chat directly with ${advisor}`,
    body: (advisor) => `Questions, special requests, or a change of plans? Message ${advisor} right inside MyTrip. Your whole conversation stays in one thread, so nothing gets lost in email.`,
  },
  {
    icon: FileText,
    img: "/welcome/documents.png",
    tag: "Travel documents",
    title: () => "All your documents in one place",
    body: () => "Boarding passes, confirmations, and itineraries are shared straight to your trip — always a tap away, whether you're at home or at the gate.",
  },
  {
    icon: User,
    img: "/welcome/profile.png",
    tag: "Your profile & travel details",
    title: () => "Keep your details ready to go",
    body: () => "Store passport info, known-traveler numbers, and loyalty programs securely in your Profile. We'll reuse them when booking, so you never have to dig them up again.",
  },
];

export default function WelcomeTour({ firstName, advisorFirst, onClose }) {
  const [i, setI] = useState(0);
  const advisor = advisorFirst || "your advisor";
  const step = STEPS[i];
  const Icon = step.icon;
  const isLast = i === STEPS.length - 1;
  const back = () => setI((n) => Math.max(0, n - 1));
  const next = () => (isLast ? onClose() : setI((n) => n + 1));

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6" data-testid="welcome-tour">
      <div className="absolute inset-0 bg-navy-950/70 backdrop-blur-sm" />
      <div
        className="relative flex w-full max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-[0_24px_80px_rgba(10,20,40,0.45)] fade-in"
        role="dialog"
        aria-modal="true"
        data-testid="welcome-tour-card"
      >
        <button
          onClick={onClose}
          data-testid="welcome-tour-skip"
          className="absolute right-4 top-4 z-10 flex items-center gap-1 rounded-full bg-white/85 px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm backdrop-blur transition-colors hover:bg-white hover:text-navy-900"
        >
          Skip <X className="h-3.5 w-3.5" />
        </button>

        <div className="relative flex h-52 items-end justify-center overflow-hidden bg-gradient-to-b from-navy-50 via-slate-50 to-white px-9 pt-8">
          <div
            key={step.img}
            className="w-full max-w-[260px] overflow-hidden rounded-t-[20px] border border-slate-200/80 bg-white shadow-[0_-6px_34px_rgba(15,30,54,0.14)] fade-in"
            data-testid="welcome-tour-image"
          >
            <img src={step.img} alt={step.tag} className="h-44 w-full object-cover object-top" draggable={false} />
          </div>
        </div>

        <div className="flex flex-col px-7 pb-6 pt-5">
          <div className="flex items-center gap-2" data-testid="welcome-tour-tag">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-navy-900 text-white">
              <Icon className="h-4 w-4" strokeWidth={2} />
            </span>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">{step.tag}</p>
          </div>
          <h2 className="mt-3 font-display text-2xl font-bold leading-tight text-navy-900" data-testid="welcome-tour-title">
            {step.title(firstName, advisor)}
          </h2>
          <p className="mt-2.5 min-h-[88px] text-sm leading-relaxed text-slate-600" data-testid="welcome-tour-body">
            {step.body(advisor)}
          </p>

          <div className="mt-4 flex items-center justify-center gap-1.5" data-testid="welcome-tour-dots">
            {STEPS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                aria-label={`Go to step ${idx + 1}`}
                data-testid={`welcome-tour-dot-${idx}`}
                className={cn("h-1.5 rounded-full transition-all", idx === i ? "w-6 bg-navy-900" : "w-1.5 bg-slate-300 hover:bg-slate-400")}
              />
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={back}
              disabled={i === 0}
              data-testid="welcome-tour-back"
              className={cn(
                "flex items-center gap-1.5 rounded-xl px-4 py-3 text-sm font-semibold transition-colors",
                i === 0 ? "pointer-events-none opacity-0" : "text-slate-600 hover:bg-slate-100"
              )}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={next}
              data-testid="welcome-tour-next"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-navy-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
            >
              {isLast ? "Explore MyTrip" : "Next"}
              {!isLast && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
